import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core'
import { PageHeaderComponent } from '../public/page-header/page-header.component'
import { PageContentComponent } from '../public/page-content/page-content.component'
import { Subscription, zip } from 'rxjs'
import { IdrInvestigationService, IdrResult } from './idr-investigation.service'
import {
  getIdrColDefs,
  getIdrColumnMetadata,
  IdrColDef,
  IdrColumnMetadata,
  IdrColumnSpec,
} from './idr-columns'
import { FilterParams, SortParams } from '../shared/utilities/http-params'
import { IdrConfigurationComponent } from './idr-configuration.component'
import { IdrDataTableComponent } from './idr-data-table.component'
import { union } from 'lodash'
import { APIEnvelope } from '../shared/models/api'
import { SortEvent, TableSortService } from '../public/table/services/table-sort.service'
import { IdrExportModalComponent } from './idr-export-modal.component'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import {
  IdrDataTableHeaderComponent,
  IdrTableHeaderMenuItem,
  IdrTableHeaderParams,
} from './idr-data-table-header.component'
import { IdrUserSettingsService } from './idr-user-settings.service'
import { ToastService } from '../public/toasts/toasts.service'

type CurrentIdrView = 'data' | 'configure'
type ColumnPredicate = (colDef: IdrColDef) => boolean

const fieldPredicate = (field: string): ColumnPredicate =>
  d => d.field === field

@Component({
  selector: 'app-idr-investigation',
  imports: [
    PageHeaderComponent,
    PageContentComponent,
    IdrConfigurationComponent,
    IdrDataTableComponent,
  ],
  template: `
    <div app-page-header header='IDR Investigation'>
      <div class='toolbar'>
        <button
            class='btn btn-outline-primary btn-sm'
            (click)="handleCurrentViewToggle()"
            [disabled]="!isViewToggleEnabled()"
        >
          {{ currentView() === 'configure' ? 'Show Results' : 'Edit Columns/Filters' }}
        </button>
        <button
            class='btn btn-outline-secondary btn-sm'
            (click)="exportData()"
            [disabled]="!configurationValid()"
        >
          Download CSV
        </button>
      </div>
    </div>
    <app-page-content>
      @if (currentView() === 'configure') {
        <app-idr-configuration
            [columnDefs]="columnDefs()"
            [filters]="filters()"
            [columnMetadata]="columnMetadata()"
            [orderBy]="sortService.getSortOrder()"
            [editedFilter]="editedFilter()"
            (columnsChange)="handleColumnDefsChange($event)"
            (filtersChange)="handleFiltersChange($event)"
            (editedFilterReset)="handleEditedFilterReset()"
        />
      } @else {
        <app-idr-data-table
            [columnDefs]="columnDefs()"
            [rowData]="data().results"
            [totalResultCount]="data().total"
            [pageNumber]="pageNumber()"
            [pageSize]="pageSize"
            [loading]="loading()"
            (pageNumberChange)="handlePageNumberChange($event)"
            (editFilters)="currentView.set('configure')"
        />
      }
    </app-page-content>
  `,
  styles: `
    .toolbar {
      padding-bottom: 1rem;

      button {
        margin-left: 1rem;
      }
    }
  `,
})
export class IdrInvestigationComponent implements OnInit, OnDestroy {
  private readonly idrService = inject(IdrInvestigationService)
  private readonly userSettingsService = inject(IdrUserSettingsService)
  private readonly toastService = inject(ToastService)
  private readonly modalService = inject(NgbModal)
  protected readonly sortService = inject(TableSortService)

  private readonly subscription = new Subscription()

  protected readonly currentView = signal<CurrentIdrView>('configure')
  protected readonly editedFilter = signal<string | undefined>(undefined)
  protected readonly data = signal<APIEnvelope<IdrResult>>({ total: 0, results: [] })
  protected readonly columnMetadata = signal<IdrColumnMetadata>({ columnSpecs: [], columnSpecsByField: {} })
  protected readonly filters = signal<readonly FilterParams[]>([])
  protected readonly pageNumber = signal(1)
  protected readonly loading = signal(true)

  private readonly sortOrder = signal<readonly SortParams[]>([
    {
      name: 'operating_date',
      direction: 'desc',
    },
  ])
  private readonly rawColumnDefs = signal<IdrColDef[]>([])

  protected readonly columnDefs = computed(
    () => this.rawColumnDefs().map(d => ({
      ...d,
      headerComponent: IdrDataTableHeaderComponent,
      headerComponentParams: this.getHeaderComponentParams(d),
    })),
  )

  protected readonly pageSize = 500

  protected readonly configurationValid = computed(() =>
    this.filters().length > 0 && this.filters().some(_ => _.name === 'operating_date'),
  )

  private readonly onSortChange = (event: SortEvent) => {
    this.sortService.next(event)
  }

  private getColumnSpec(field: string): IdrColumnSpec {
    return this.columnMetadata().columnSpecsByField[field]
  }

  private getColumnMenuItems(
    colDef: IdrColDef,
    filtered: boolean,
    filterCount: number,
  ): readonly IdrTableHeaderMenuItem[] {
    const columnSpec = this.getColumnSpec(colDef.field)
    const items: IdrTableHeaderMenuItem[] = []
    items.push({
      id: 'configure-filter',
      label: filtered ? 'Edit Filter ...' : 'Filter ...',
      click: () => {
        this.editFilter(colDef.field)
      },
    })
    if (filtered && filterCount > 1) {
      items.push({
        id: 'remote-filter',
        label: 'Clear Filter',
        click: () => {
          this.clearFilter(colDef.field)
        },
      })
    }
    if (columnSpec.hourlyUsage) {
      items.push({
        id: 'remove-hourly-columns',
        label: 'Remove Hourly Usage Columns',
        click: () => {
          this.removeColumns(d => Boolean(this.getColumnSpec(d.field).hourlyUsage))
        },
      })
    } else if (columnSpec.columnType !== 'required') {
      items.push({
        id: 'remove-column',
        label: 'Remove Column',
        click: () => {
          this.removeColumns(fieldPredicate(colDef.field))
        },
      })
    }
    items.push({
      id: 'configure',
      label: 'Edit Columns/Filters',
      separator: true,
      click: () => {
        this.currentView.set('configure')
      },
    })
    return items
  }

  private getHeaderComponentParams(colDef: IdrColDef): IdrTableHeaderParams {
    const filters = this.filters()
    const filtered = filters.some(_ => _.name === colDef.field)
    const currentSort = this.sortOrder().find(_ => _.name === colDef.field)
    return {
      filtered,
      ...currentSort ? { sortDirection: currentSort.direction ?? 'asc' } : {},
      menu: { items: this.getColumnMenuItems(colDef, filtered, filters.length) },
      onSortChange: this.onSortChange,
    }
  }

  protected isViewToggleEnabled() {
    return this.currentView() === 'data' || this.configurationValid()
  }

  protected handleCurrentViewToggle() {
    this.currentView.set(this.currentView() === 'data' ? 'configure' : 'data')
    this.getDataIfNeeded()
  }

  protected handleColumnDefsChange(newValue: IdrColDef[]) {
    this.rawColumnDefs.set(newValue)
    this.userSettingsService.setColumnVisibility(newValue, this.columnMetadata())
    this.getDataIfNeeded()
  }

  protected handlePageNumberChange(newValue: number) {
    this.pageNumber.set(newValue)
    this.getDataIfNeeded()
  }

  protected handleFiltersChange(newValue: readonly FilterParams[]) {
    this.filters.set(newValue)
    this.getDataIfNeeded()
  }

  protected handleEditedFilterReset() {
    this.editedFilter.set(undefined)
  }

  private getColumnsForServiceCall(predicate: (colDef: IdrColDef) => boolean): readonly string[] {
    return union(
      this.columnDefs().filter(predicate).map(_ => _.field),
      this.filters().map(_ => _.name),
    ).sort()
  }

  protected getDataIfNeeded() {
    if (this.currentView() === 'data') {
      this.loading.set(true)
      const dataObservable = this.idrService.getResults(
        this.getColumnsForServiceCall(c =>
          !c.hide || Boolean(this.columnMetadata().columnSpecsByField[c.field].hourlyUsage),
        ),
        this.filters(),
        this.sortService.getSortOrder(),
        { pageNumber: this.pageNumber(), pageSize: this.pageSize },
      )
      this.subscription.add(dataObservable.subscribe({
        next: data => {
          this.loading.set(false)
          if (!data.cached) {
            this.data.set(data)
          }
        },
        error: error => {
          this.loading.set(false)
          this.toastService.showError(error)
        },
      }))
    }
  }

  protected exportData() {
    const modalRef = this.modalService.open(
      IdrExportModalComponent,
      { centered: true, backdrop: 'static' },
    )
    const instance: IdrExportModalComponent = modalRef.componentInstance
    instance.init({
      columns: this.getColumnsForServiceCall(c => !c.hide),
      filters: this.filters(),
      order: this.sortService.getSortOrder(),
    })
  }

  private removeColumns(predicate: ColumnPredicate) {
    this.handleColumnDefsChange(this.columnDefs().map(d => predicate(d) ? { ...d, hide: true } : d))
  }

  private editFilter(field: string) {
    this.editedFilter.set(field)
    this.currentView.set('configure')
  }

  private clearFilter(field: string) {
    this.handleFiltersChange(this.filters().filter(_ => _.name !== field))
  }

  ngOnInit() {
    const columnsObservable = zip(
      this.idrService.getColumns(),
      this.userSettingsService.getColumnVisibility(),
    )
    this.subscription.add(
      columnsObservable.subscribe(([response, visibility]) => {
        const columnMetadata = getIdrColumnMetadata(response)
        this.columnMetadata.set(columnMetadata)
        this.rawColumnDefs.set(getIdrColDefs(columnMetadata.columnSpecs, visibility))
        this.getDataIfNeeded()
      }),
    )
    this.sortService.setSortOrder(this.sortOrder())
    this.subscription.add(this.sortService.getOrder().subscribe(order => {
      this.sortOrder.set(order)
      this.getDataIfNeeded()
    }))
    this.subscription.add(
      this.userSettingsService.getFilters().subscribe(params => {
        this.filters.set(params)
      }),
    )
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
    this.idrService.clearCache()
  }
}
